import React, { useState, useRef } from 'react';
import { useRelatedProducts } from '@algolia/recommend-react';
import recommend from '@algolia/recommend';
import { Link } from 'react-router-dom';
import { Wine, Star, MapPin, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import { ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY } from '../../services/algoliaClient';
import { insights } from '../../services/algoliaInsights';
import { useStore } from '../../context/StoreContext';
import { recordEvent } from '../../utils/userProfile';

// Create the recommend client (same credentials as searchClient)
const recommendClient = recommend(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY);

// Custom hook to safely get index name
function useIndexName() {
  // Always return the default index name for now
  // This avoids the conditional hook call issue
  return 'saq_products';
}

// Beautiful Product Card Component
function RecommendProductCard({ item, indexName = 'saq_products' }) {
  const { selectedStore } = useStore();
  const handleClick = () => {
    // Track recommendation click event
    insights.clickedObjectIDs(
      'Recommendation Click',
      indexName,
      [item.objectID],
      item.price || 0
    );
    recordEvent({
      type: 'click',
      product: { ...item, selectedStore: String(selectedStore || '1') },
    });
  };

  return (
    <Link 
      to={`/produit/${item.objectID}`} 
      onClick={handleClick}
      style={{
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        color: 'inherit',
        background: '#fff',
        borderRadius: 16,
        padding: 20,
        boxShadow: '0 4px 20px rgba(75,22,76,0.08)',
        border: '1px solid #f0f0f0',
        transition: 'all 0.3s ease',
        height: '100%',
        width: '280px',
        flexShrink: 0
      }}
      onMouseOver={e => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 32px rgba(75,22,76,0.15)';
        e.currentTarget.style.borderColor = '#4B164C';
      }}
      onMouseOut={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 20px rgba(75,22,76,0.08)';
        e.currentTarget.style.borderColor = '#f0f0f0';
      }}
    >
      {/* Product Image */}
      <div style={{ 
        textAlign: 'center', 
        marginBottom: 16,
        background: 'linear-gradient(135deg, #f8f6fa 0%, #f0e8f2 100%)',
        borderRadius: 12,
        padding: 20,
        minHeight: 140,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {item.image ? (
          <img 
            src={item.image.startsWith('http') ? item.image : `https:${item.image}`} 
            alt={item.name}
            style={{ 
              maxWidth: '100%', 
              maxHeight: 120, 
              objectFit: 'contain',
              borderRadius: 8,
              transition: 'transform 0.3s ease'
            }}
            onMouseOver={e => {
              e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.transform = 'scale(1)';
            }}
          />
        ) : (
          <Wine size={48} color="#4B164C" />
        )}
      </div>

      {/* Product Info */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h4 style={{ 
          fontSize: 16, 
          fontWeight: 600, 
          color: '#4B164C', 
          marginBottom: 12,
          lineHeight: 1.4,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          minHeight: '44px'
        }}>
          {item.name}
        </h4>

        {/* Product Attributes */}
        <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {item.attributes?.pays_origine && (
            <div style={{ 
              fontSize: 13, 
              color: '#666',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}>
              <MapPin size={14} />
              {item.attributes.pays_origine}
              {item.attributes.region_origine && `, ${item.attributes.region_origine}`}
            </div>
          )}
          
          {item.attributes?.cepage && (
            <div style={{ 
              fontSize: 13, 
              color: '#888',
              fontStyle: 'italic'
            }}>
              {item.attributes.cepage}
            </div>
          )}
        </div>

        {/* Price and Rating */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginTop: 'auto',
          paddingTop: 12,
          borderTop: '1px solid #f0f0f0'
        }}>
          <div style={{ 
            fontSize: 18, 
            fontWeight: 700, 
            color: '#4B164C',
            background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {item.price ? `${item.price.toLocaleString('fr-CA', { minimumFractionDigits: 2 })} $` : ''}
          </div>
          
          {item.customer_rating && (
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 4,
              fontSize: 13,
              color: '#4B164C',
              background: '#f8f6fa',
              padding: '4px 8px',
              borderRadius: 12
            }}>
              <Star size={14} weight="fill" />
              <span style={{ fontWeight: 600 }}>{item.customer_rating}</span>
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

// Custom Carousel Wrapper
function CarouselWrapper({ children }) {
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = scrollContainerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      scrollContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  React.useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, [children]);

  return (
    <div style={{ position: 'relative' }}>
      {/* Left Arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          style={{
            position: 'absolute',
            left: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            boxShadow: '0 4px 12px rgba(75,22,76,0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={e => {
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
        >
          <ArrowLeft size={20} color="#fff" />
        </button>
      )}

      {/* Right Arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          style={{
            position: 'absolute',
            right: -20,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
            border: 'none',
            borderRadius: '50%',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            boxShadow: '0 4px 12px rgba(75,22,76,0.3)',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={e => {
            e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
          }}
          onMouseOut={e => {
            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
          }}
        >
          <ArrowRight size={20} color="#fff" />
        </button>
      )}

      {/* Scrollable Container */}
      <div
        ref={scrollContainerRef}
        style={{
          display: 'flex',
          gap: 20,
          overflowX: 'auto',
          padding: '8px 0',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          scrollBehavior: 'smooth'
        }}
        onScroll={checkScrollButtons}
      >
        <style>
          {`
            div::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
        {children}
      </div>
    </div>
  );
}

// Main Recommend Widget Component - Beautiful Carousel
export const RecommendWidget = ({ objectID, title = "Vous pourriez aussi aimer", maxRecommendations = 4 }) => {
  const [fallbackItems, setFallbackItems] = useState([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [fallbackError, setFallbackError] = useState(null);
  
  // Get the current index name using our custom hook
  const currentIndexName = useIndexName();

  const { items, isSearchStalled } = useRelatedProducts({
    recommendClient,
    indexName: currentIndexName,
    objectIDs: [String(objectID)],
    maxRecommendations,
    queryParameters: {
      clickAnalytics: true,
    },
  });

  // Fallback: Direct API call if hook doesn't work
  React.useEffect(() => {
    if (!objectID) return;

    const fetchFallback = async () => {
      try {
        setFallbackLoading(true);
        setFallbackError(null);
        
        const response = await recommendClient.getRelatedProducts([
          {
            indexName: currentIndexName,
            objectID: String(objectID),
            threshold: 0,
            queryParameters: {
              clickAnalytics: true,
            },
          },
        ]);
        
        if (response.results && response.results[0] && response.results[0].hits) {
          setFallbackItems(response.results[0].hits);
        }
      } catch (err) {
        console.error('Fallback: API error:', err);
        setFallbackError(err.message);
      } finally {
        setFallbackLoading(false);
      }
    };

    // Only try fallback if hook returns empty after a delay
    const timer = setTimeout(() => {
      if (!items || items.length === 0) {
        fetchFallback();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [objectID, items]);

  // Use fallback items if hook items are empty
  const finalItems = (items && items.length > 0) ? items : fallbackItems;
  const finalLoading = isSearchStalled || fallbackLoading;


  if (!objectID) {
    return null;
  }

  if (finalLoading) {
    return (
      <div style={{ 
        background: 'linear-gradient(135deg, #f8f6fa 0%, #f0e8f2 100%)', 
        borderRadius: 24, 
        padding: 32, 
        marginTop: 40,
        boxShadow: '0 8px 32px rgba(75,22,76,0.12)',
        border: '1px solid rgba(75,22,76,0.08)',
        textAlign: 'center'
      }}>
        <div style={{ color: '#4B164C', fontSize: 16 }}>Chargement des recommandations...</div>
      </div>
    );
  }

  // Show debug info if no items
  if (!finalItems || finalItems.length === 0) {
    return (
      <div style={{ 
        background: 'linear-gradient(135deg, #f8f6fa 0%, #f0e8f2 100%)', 
        borderRadius: 24, 
        padding: 32, 
        marginTop: 40,
        boxShadow: '0 8px 32px rgba(75,22,76,0.12)',
        border: '1px solid rgba(75,22,76,0.08)',
        textAlign: 'center'
      }}>
        <div style={{ color: '#4B164C', fontSize: 16, marginBottom: 16 }}>
          {title}
        </div>
        <div style={{ color: '#666', fontSize: 14 }}>
          Debug: No recommendations found for objectID: {objectID}
        </div>
        <div style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
          Hook Items: {items ? items.length : 'undefined'}<br/>
          Fallback Items: {fallbackItems ? fallbackItems.length : 'undefined'}<br/>
          Hook Loading: {String(isSearchStalled)}, Fallback Loading: {String(fallbackLoading)}
        </div>
        {fallbackError && (
          <div style={{ color: '#d32f2f', fontSize: 12, marginTop: 8 }}>
            Fallback Error: {fallbackError}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #f8f6fa 0%, #f0e8f2 100%)', 
      borderRadius: 24, 
      padding: 32, 
      marginTop: 40,
      boxShadow: '0 8px 32px rgba(75,22,76,0.12)',
      border: '1px solid rgba(75,22,76,0.08)'
    }}>
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: 28,
        gap: 12
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
          borderRadius: 12,
          padding: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Wine size={24} color="#fff" />
        </div>
        <h3 style={{ 
          fontSize: 24, 
          color: '#4B164C', 
          fontWeight: 700,
          margin: 0,
          background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          {title}
        </h3>
      </div>
      
      {/* Carousel Container */}
      <CarouselWrapper>
        {finalItems.map((item, index) => (
          <RecommendProductCard key={item.objectID || index} item={item} indexName={currentIndexName} />
        ))}
      </CarouselWrapper>
    </div>
  );
};
